/**
 * What the feedback forms say when the feedback limit refuses a submission (#119).
 *
 * The feedback endpoints take a few submissions a minute from one sender. The
 * limiter (fastapi-limiter) refuses the next with 429, the bare detail "Too Many
 * Requests" and a Retry-After header: the seconds until the sender may submit
 * again. Those three words reached the user as they were — no advice, not
 * translated. This turns them into a translated sentence that says when to try
 * again: in the seconds Retry-After gives, or in a minute when it gives none.
 *
 * A 429 that gives a reason of its own is not this refusal, and is left to the
 * caller: the daily attachment quota says how much may be attached and when the
 * file may be attached again, and "try again in a minute" would be wrong advice.
 */

// The detail the limiter's own refusal carries (fastapi_limiter's default callback).
const LIMITER_DETAIL = "Too Many Requests";

/**
 * @param {*} error - what the feedback mutation rejected with; `retryAfter` is the
 *   Retry-After header, which feedbackService adds to the error.
 * @param {Function} t - i18next's t, with the "feedback" namespace
 * @returns {string|null} the sentence for the limiter's refusal; null otherwise
 */
export const feedbackLimitMessage = (error, t) => {
  if (error?.status !== 429) return null;
  const detail = error.data?.detail;
  if (detail && detail !== LIMITER_DETAIL) return null;
  const seconds = retryAfterSeconds(error.retryAfter);
  return seconds
    ? t("feedback:tooManySubmissionsRetryIn", { count: seconds })
    : t("feedback:tooManySubmissions");
};

// Retry-After is a number of seconds or an HTTP date (RFC 9110); the limiter sends
// seconds. Anything unreadable or already past says nothing.
const retryAfterSeconds = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  const seconds = /^\d+$/.test(text)
    ? Number(text)
    : Math.ceil((Date.parse(text) - Date.now()) / 1000);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
};
